import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import React, { memo, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useScreenSize from '../../util/hooks/useScreenSize';
import RatingDistributionCard from './RatingDistributionCard';
import Review from './Review';

const EmptyReviewElement = () => <Box />;

/**
 * Displays a list of reviews with rating distribution and an option to show all reviews.
 */
const Reviews = ({ reviews = [], getTranslation }) => {
  const [showAll, setShowAll] = useState(false); // State variable to toggle view

  const handleShowAll = () => {
    setShowAll(!showAll); // Toggle the state variable
  };

  const calculateStarDistribution = (reviews) => {
    // Initialize an array with 5 zeros
    const distribution = Array(5).fill(0);

    reviews.forEach((review) => {
      const rating = review.attributes.rating || review.rating;
      // Increment the count for the given rating
      distribution[rating - 1]++;
    });

    return distribution;
  };

  const starDistribution = useMemo(() => calculateStarDistribution(reviews), [reviews]);

  // Calculate the average review score
  const totalReviews = reviews.length;
  const totalScore = reviews.reduce((acc, review) => acc + review.attributes.rating, 0);
  const averageReview = totalReviews ? totalScore / totalReviews : 0;

  const elements = [...reviews];

  // always place RatingDistributionCard as the 5th element
  // if reviews are less then 5 then fill the rest with EmptyReviewElement
  // always place LoadMoreReviewsElement as the last element

  if (reviews.length < 5) {
    for (let i = 0; i < 4 - reviews.length; i++) {
      elements.push(<EmptyReviewElement key={`EmptyReviewElement_${i}`} />);
    }
    elements.push(
      <RatingDistributionCard
        averageReview={averageReview}
        reviewCount={totalReviews}
        starDistribution={starDistribution}
      />,
    );
  } else if (reviews.length >= 5) {
    // place the RatingDistributionCard as the 5th element
    const removedElement = elements.splice(
      4,
      0,
      <RatingDistributionCard
        averageReview={averageReview}
        reviewCount={totalReviews}
        starDistribution={starDistribution}
      />,
    );
    elements.push(removedElement);
  }

  //const elementsToShow = showAll ? elements : elements.slice(0, 5);

  const { isMobile, isTablet } = useScreenSize();

  const mobile = (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Box pb={4}>
          <RatingDistributionCard
            averageReview={averageReview}
            reviewCount={totalReviews}
            starDistribution={starDistribution}
          />
        </Box>
      </Grid>
      <Grid container spacing={4}>
        {reviews.slice(0, showAll ? reviews.length : 5).map(
          (
            r,
            index, // Adjust this line
          ) => (
            <Review getTranslation={getTranslation} key={index} review={r} />
          ),
        )}
      </Grid>
      {reviews.length > 5 ? (
        <Grid size={12}>
          <Button
            endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreOutlinedIcon />}
            onClick={handleShowAll}
            sx={{ width: { xs: 1, md: 'auto' }, mt: { xs: 6, md: 0 } }}
            variant="outlined"
          >
            {showAll ? (
              <FormattedMessage id="StoryblokArchiveBlog.showLess" />
            ) : (
              <FormattedMessage id="StoryblokArchiveBlog.showAll" />
            )}
          </Button>
        </Grid>
      ) : null}
    </Grid>
  );

  const desktop = (
    <Grid container size={12} spacing={2}>
      <Grid container flexGrow={1} size={{ md: 9, xs: 12 }} spacing={4}>
        {reviews.slice(0, showAll ? reviews.length : 5).map(
          (
            r,
            index, // Adjust this line
          ) => (
            <Review getTranslation={getTranslation} key={index} review={r} />
          ),
        )}
      </Grid>
      <Grid size={{ md: 3, xs: 12 }}>
        <Box pb={4}>
          <RatingDistributionCard
            averageReview={averageReview}
            reviewCount={totalReviews}
            starDistribution={starDistribution}
          />
        </Box>
      </Grid>
      {reviews.length > 5 ? (
        <Grid size={12}>
          <Button
            endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreOutlinedIcon />}
            onClick={handleShowAll}
            sx={{ width: { xs: 1, md: 'auto' }, mt: { xs: 6, md: 0 } }}
            variant="outlined"
          >
            {showAll ? (
              <FormattedMessage id="StoryblokArchiveBlog.showLess" />
            ) : (
              <FormattedMessage id="StoryblokArchiveBlog.showAll" />
            )}
          </Button>
        </Grid>
      ) : null}
    </Grid>
  );

  return isTablet || isMobile ? mobile : desktop;
};

export default memo(Reviews);
